import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { VrboService } from '@app/_services/vrbo.service';
import * as moment from 'moment';

type ViewMode = 'SINGLE' | 'MULTI' | 'LIST';
type StatusFilter = 'ACTIVE' | 'INACTIVE' | 'FUTURE' | 'EXPIRED' | 'ALL';
type SourceFilter = 'ALL' | 'VRBO' | 'WIKI';

interface DashboardProperty {
  propertyId: string;
  propertyCode: string;
  name: string;
  promotionCount: number;
  activeTypes: string[];
  loaded: boolean;
}

interface DashboardPromotion {
  id: string;
  propertyId: string;
  propertyCode: string;
  propertyName: string;
  promotionType: string;
  status: string;
  incentive: any;
  restrictions: any;
  couponCode?: string;
  couponName?: string;
  isAutoApplied?: boolean;
  referralSource?: string;
  discountValue?: number;
  discountUnit?: string;
  bookingWindowFrom?: string;
  bookingWindowTo?: string;
}

@Component({
    selector: 'app-vrbo-promotions-dashboard',
    templateUrl: './vrbo-promotions-dashboard.component.html',
    styleUrls: ['./vrbo-promotions-dashboard.component.css'],
    standalone: false
})
export class VRBOPromotionsDashboardComponent implements OnInit {
  showCreatePromotion = false;
  properties: DashboardProperty[] = [];
  selectedPropertyId: string | null = null;
  promotionsByProperty: { [propertyId: string]: DashboardPromotion[] } = {};
  globalWikiCoupons: DashboardPromotion[] = [];

  loadingProperties = false;
  loadingPromotions = false;

  propertySearch = '';
  withPromotionsOnly = false;
  missingPromotionType: string | null = null;

  viewMode: ViewMode = 'LIST';
  statusFilter: StatusFilter = 'ACTIVE';
  selectedPromotionTypes: string[] = [];
  sourceFilter: SourceFilter = 'ALL';
  groupByProperty = false;

  selectedPromotion: DashboardPromotion | null = null;
  editingPromotion: DashboardPromotion | null = null;
  detailVisible = false;

  currentMonth = moment();
  multiDayColumns = Array.from({ length: 14 }, (_, i) => i);

  promotionTypeOptions = [
    { label: 'Early Booking', value: 'EARLY_BOOKING' },
    { label: 'Last Minute', value: 'LAST_MINUTE' },
    { label: 'New Listing', value: 'NEW_LISTING_DISCOUNT' },
    { label: 'Member Only', value: 'MEMBER_ONLY' },
    { label: 'Mobile Only', value: 'MOBILE_ONLY' },
    { label: 'Wiki Coupon', value: 'WIKI_COUPON' }
  ];

  promotionColorMap: { [key: string]: string } = {
    EARLY_BOOKING: '#4285F4',
    LAST_MINUTE: '#F4A742',
    NEW_LISTING_DISCOUNT: '#34A853',
    MEMBER_ONLY: '#A142F4',
    MOBILE_ONLY: '#00BCD4',
    WIKI_COUPON: '#E91E63'
  };

  // Bulk deactivate state
  bulkDeactivating = false;
  bulkDeactivateProgress = 0;
  bulkDeactivateTotal = 0;

  constructor(
    private vrboService: VrboService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Keep old direct URL working by opening create mode in dashboard.
    if (this.router.url.includes('/vrbo-promotions') && !this.router.url.includes('/vrbo-promotions-dashboard')) {
      this.showCreatePromotion = true;
    }
    this.loadDashboardData();
  }

  loadDashboardData(): void {
    this.loadingProperties = true;
    this.loadingPromotions = true;
    this.vrboService.queryPromotionsDashboard({ includeLivePromotions: false }).subscribe(
      (res: any) => {
        const dashboard = res?.data?.promotionsDashboard || {};
        const props = dashboard?.properties || [];
        const promotionsMap = dashboard?.promotionsByProperty || {};
        this.properties = props.map((p: any) => ({
          propertyId: (p.propertyId || '').toString(),
          propertyCode: p.propertyCode || '',
          name: p.name || p.propertyCode || ('Property ' + (p.propertyId || '')),
          promotionCount: p.activePromotionCount || 0,
          activeTypes: p.activeTypes || [],
          loaded: true
        })).filter((p: DashboardProperty) => !!p.propertyId);

        // Map global wiki coupons once (sent separately to avoid per-property duplication).
        const globalWikis = dashboard?.globalWikiCoupons || [];
        this.globalWikiCoupons = globalWikis.map((e: any) => ({
          id: e?.id || '',
          propertyId: '',
          propertyCode: '',
          propertyName: 'All Properties',
          promotionType: e?.promotionType || 'WIKI_COUPON',
          status: e?.status || 'INACTIVE',
          incentive: e?.incentive || {},
          restrictions: e?.restrictions || {},
          couponCode: e?.couponCode || undefined,
          couponName: e?.couponName || undefined,
          isAutoApplied: e?.isAutoApplied || false,
          referralSource: e?.referralSource || 'WIKI',
          discountValue: e?.discountValue || undefined,
          discountUnit: e?.discountUnit || undefined,
          bookingWindowFrom: e?.bookingWindowFrom || undefined,
          bookingWindowTo: e?.bookingWindowTo || undefined
        }));

        // Map property-specific promotions (no longer includes wiki coupons).
        this.promotionsByProperty = {};
        this.properties.forEach((prop: DashboardProperty) => {
          const items = promotionsMap[prop.propertyId] || [];
          this.promotionsByProperty[prop.propertyId] = items.map((e: any) => ({
            id: e?.id || '',
            propertyId: prop.propertyId,
            propertyCode: prop.propertyCode,
            propertyName: prop.name,
            promotionType: e?.promotionType || '',
            status: e?.status || 'INACTIVE',
            incentive: e?.incentive || {},
            restrictions: e?.restrictions || {},
            couponCode: e?.couponCode || undefined,
            couponName: e?.couponName || undefined,
            isAutoApplied: e?.isAutoApplied || false,
            referralSource: e?.referralSource || undefined,
            discountValue: e?.discountValue || undefined,
            discountUnit: e?.discountUnit || undefined,
            bookingWindowFrom: e?.bookingWindowFrom || undefined,
            bookingWindowTo: e?.bookingWindowTo || undefined
          }));
        });

        this.loadingProperties = false;
        this.loadingPromotions = false;
        if (!this.selectedPropertyId && this.filteredProperties.length) {
          this.selectProperty(this.filteredProperties[0]);
        }
      },
      (err: any) => {
        this.loadingProperties = false;
        this.loadingPromotions = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load promotions dashboard: ' + (err?.message || err) });
      }
    );
  }

  get missingPropertiesSorted(): DashboardProperty[] {
    return [...this.filteredProperties].sort((a, b) => {
      if (a.promotionCount !== b.promotionCount) return b.promotionCount - a.promotionCount;
      return a.name.localeCompare(b.name);
    });
  }

  getActiveTypeLabels(p: DashboardProperty): string {
    if (!p.activeTypes.length) return '--';
    return p.activeTypes.map((t: string) => this.getPromotionTypeLabel(t)).join(', ');
  }

  getCoverage(p: DashboardProperty): string {
    return `${p.activeTypes.length} of ${this.promotionTypeOptions.length}`;
  }

  get filteredPropertiesBase(): DashboardProperty[] {
    const text = (this.propertySearch || '').trim().toLowerCase();
    return this.properties.filter((p: DashboardProperty) => {
      const matchesSearch = !text || p.name.toLowerCase().includes(text) || p.propertyId.toLowerCase().includes(text) || p.propertyCode.toLowerCase().includes(text);
      const matchesWithPromotions = !this.withPromotionsOnly || p.promotionCount > 0;
      return matchesSearch && matchesWithPromotions;
    });
  }

  get filteredProperties(): DashboardProperty[] {
    const missingVal = this.resolvedMissingType;
    if (!missingVal) return this.filteredPropertiesBase;
    return this.filteredPropertiesBase.filter((p: DashboardProperty) => !p.activeTypes.includes(missingVal));
  }

  selectProperty(p: DashboardProperty): void {
    this.selectedPropertyId = p.propertyId;
    if (this.viewMode === 'MULTI') {
      this.viewMode = 'SINGLE';
    }
  }

  get selectedPropertyPromotions(): DashboardPromotion[] {
    if (!this.selectedPropertyId) return [];
    const propertyPromos = this.promotionsByProperty[this.selectedPropertyId] || [];
    const merged = [...propertyPromos, ...this.globalWikiCoupons];
    return this.applyPromotionFilters(merged);
  }

  get allVisiblePromotions(): DashboardPromotion[] {
    const visibleIds = new Set(this.filteredPropertiesBase.map((p: DashboardProperty) => p.propertyId));
    const all = Object.keys(this.promotionsByProperty).reduce((acc: DashboardPromotion[], k: string) => {
      return acc.concat(this.promotionsByProperty[k] || []);
    }, []);
    const filtered = all.filter((p: DashboardPromotion) => visibleIds.has(p.propertyId));
    // Add global wiki coupons once (not per-property) — source filter handles WIKI vs VRBO
    const merged = [...filtered, ...this.globalWikiCoupons];
    return this.applyPromotionFilters(merged);
  }

  applyPromotionFilters(items: DashboardPromotion[]): DashboardPromotion[] {
    return items.filter((p: DashboardPromotion) => {
      const statusOk = this.statusFilter === 'ALL' || p.status === this.statusFilter;
      const typeOk = !this.selectedPromotionTypes.length || this.selectedPromotionTypes.includes(p.promotionType);
      const sourceOk = this.sourceFilter === 'ALL'
        || (this.sourceFilter === 'WIKI' && p.referralSource === 'WIKI')
        || (this.sourceFilter === 'VRBO' && (!p.referralSource || p.referralSource === 'VRBO'));
      return statusOk && typeOk && sourceOk;
    });
  }

  openDetail(p: DashboardPromotion): void {
    this.selectedPromotion = p;
    this.detailVisible = true;
  }

  closeDetail(): void {
    this.detailVisible = false;
    this.selectedPromotion = null;
  }

  get resolvedMissingType(): string | null {
    if (!this.missingPromotionType) return null;
    return typeof this.missingPromotionType === 'string'
      ? this.missingPromotionType
      : (this.missingPromotionType as any)?.value || null;
  }

  getPromotionTypeLabel(type: string): string {
    const found = this.promotionTypeOptions.find((t: any) => t.value === type);
    return found ? found.label : type;
  }

  /** True if this promotion is a wiki coupon. */
  isWikiCoupon(p: DashboardPromotion): boolean {
    return p?.promotionType === 'WIKI_COUPON';
  }

  /**
   * True if this wiki coupon applies to all properties (was created without selecting
   * specific properties). Globals come back via the `globalWikiCoupons` array, where the
   * mapping sets propertyName='All Properties' and propertyId=''.
   */
  isGlobalWikiCoupon(p: DashboardPromotion): boolean {
    if (!this.isWikiCoupon(p)) return false;
    return !p.propertyId || p.propertyName === 'All Properties';
  }

  /** Short scope label for the badge in the Type column / detail header. */
  getWikiScopeLabel(p: DashboardPromotion): string {
    return this.isGlobalWikiCoupon(p) ? 'Global' : 'Specific';
  }

  getPromotionColor(type: string): string {
    return this.promotionColorMap[type] || '#9AA0A6';
  }

  isActive(status: string): boolean {
    return (status || '').toUpperCase() === 'ACTIVE';
  }

  unique(values: string[]): string[] {
    return Array.from(new Set(values));
  }

  previousMonth(): void {
    this.currentMonth = this.currentMonth.clone().subtract(1, 'month');
  }

  nextMonth(): void {
    this.currentMonth = this.currentMonth.clone().add(1, 'month');
  }

  goToday(): void {
    this.currentMonth = moment();
  }

  get monthLabel(): string {
    return this.currentMonth.format('MMMM YYYY');
  }

  getMonthDays(): moment.Moment[] {
    const start = this.currentMonth.clone().startOf('month').startOf('week');
    const days: moment.Moment[] = [];
    for (let i = 0; i < 42; i++) {
      days.push(start.clone().add(i, 'day'));
    }
    return days;
  }

  isToday(day: moment.Moment): boolean {
    return day.isSame(moment(), 'day');
  }

  getPromotionsForDay(day: moment.Moment): DashboardPromotion[] {
    const src = this.selectedPropertyPromotions;
    return src.filter((p: DashboardPromotion) => {
      const from = p.restrictions?.travelDateRange?.from ? moment(p.restrictions.travelDateRange.from) : null;
      const to = p.restrictions?.travelDateRange?.to ? moment(p.restrictions.travelDateRange.to) : null;
      if (!from || !to) return false;
      return day.isBetween(from, to, 'day', '[]');
    });
  }

  /**
   * Calculates the rate markup percentage for a promotion.
   * Formula: markup = (commission + discount) / (1 - commission - discount)
   * where commission = 8% (VRBO) and discount is read from the promotion.
   * Mirrors the backend logic in VrboLodgingRate.calculateMarkupForDate().
   */
  getMarkupText(p: DashboardPromotion): string {
    const VRBO_COMMISSION = 0.08;
    const BASE_MARKUP = 9;

    if (p.promotionType === 'WIKI_COUPON') {
      return 'N/A';
    }

    const discount = p.incentive?.discountIncentive?.discount || {};
    const single = discount?.single?.singleDiscount;

    if (!single?.value || single.unit !== 'PERCENT') {
      return `${BASE_MARKUP}%`;
    }

    const discountRate = single.value / 100;
    const totalExposure = VRBO_COMMISSION + discountRate;
    if (totalExposure >= 1) {
      return `${BASE_MARKUP}%`;
    }

    const markup = (totalExposure / (1 - totalExposure)) * 100;
    return `${markup.toFixed(2)}%`;
  }

  /**
   * Builds a tooltip showing the markup formula breakdown.
   * Example: "Commission: 8% + Discount: 5% = 13% exposure\nMarkup: 13% / (1 - 13%) = 14.94%"
   */
  getMarkupTooltip(p: DashboardPromotion): string {
    const VRBO_COMMISSION = 0.08;
    const BASE_MARKUP = 9;

    const discount = p.incentive?.discountIncentive?.discount || {};
    const single = discount?.single?.singleDiscount;

    if (!single?.value || single.unit !== 'PERCENT') {
      return `Base markup: ${BASE_MARKUP}% (covers ~8% VRBO commission + fees)\nNo active PERCENT promotion applies.`;
    }

    const discountPct = single.value;
    const discountRate = discountPct / 100;
    const commissionPct = VRBO_COMMISSION * 100;
    const totalExposurePct = commissionPct + discountPct;
    const totalExposure = VRBO_COMMISSION + discountRate;

    if (totalExposure >= 1) {
      return `Commission: ${commissionPct}% + Discount: ${discountPct}% = ${totalExposurePct}% exposure\n⚠ Exposure too high, using base ${BASE_MARKUP}%`;
    }

    const markup = (totalExposure / (1 - totalExposure)) * 100;
    return `Commission: ${commissionPct}% + Discount: ${discountPct}% = ${totalExposurePct}% exposure\nMarkup: ${totalExposurePct}% / (100% - ${totalExposurePct}%) = ${markup.toFixed(2)}%`;
  }

  getDiscountText(p: DashboardPromotion): string {
    const discount = p.incentive?.discountIncentive?.discount || {};
    const single = discount?.single?.singleDiscount;
    const dow = discount?.dayOfWeek;
    const tiered = discount?.tiered?.tiers;
    if (single?.value != null) {
      return single.unit === 'PERCENT' ? `${single.value}%` : `${single.value}`;
    }
    if (dow) return 'Day-of-Week';
    if (Array.isArray(tiered) && tiered.length) {
      return 'Tiered';
    }
    if (p.promotionType === 'NEW_LISTING_DISCOUNT') return '20% (default)';
    return '--';
  }

  formatDateUS(dateStr: string): string {
    if (!dateStr) return '';
    const m = moment(dateStr, 'YYYY-MM-DD', true);
    return m.isValid() ? m.format('MM/DD/YYYY') : dateStr;
  }

  getTravelText(p: DashboardPromotion): string {
    const from = p.restrictions?.travelDateRange?.from;
    const to = p.restrictions?.travelDateRange?.to;
    if (!from || !to) return '--';
    return `${this.formatDateUS(from)} - ${this.formatDateUS(to)}`;
  }

  getBookingWindowText(p: DashboardPromotion): string {
    // Wiki coupons use a calendar date range (bookingWindowFrom / bookingWindowTo).
    // VRBO promotions (Early Booking, Last Minute, etc.) use an advance-booking days range.
    if (this.isWikiCoupon(p) || p.bookingWindowFrom || p.bookingWindowTo) {
      const from = p.bookingWindowFrom;
      const to = p.bookingWindowTo;
      if (!from && !to) return '--';
      // Strip time portion from datetime strings (e.g. "2026-05-01T00:00" → "2026-05-01")
      const stripTime = (s?: string) => s ? s.split('T')[0] : '';
      return `${this.formatDateUS(stripTime(from))} - ${this.formatDateUS(stripTime(to))}`;
    }
    const min = p.restrictions?.advanceBookingDaysRange?.min;
    const max = p.restrictions?.advanceBookingDaysRange?.max;
    if (min == null || max == null) return '--';
    return `${min}-${max} days`;
  }

  getStayText(p: DashboardPromotion): string {
    const min = p.restrictions?.stayDurationRange?.min;
    const max = p.restrictions?.stayDurationRange?.max;
    if (min == null || max == null) return '--';
    return `${min}-${max}`;
  }

  getBlackoutCount(p: DashboardPromotion): number {
    return (p.restrictions?.blackoutDates || []).length;
  }

  createPromotion(): void {
    this.editingPromotion = null;
    this.showCreatePromotion = true;
    this.detailVisible = false;
    this.selectedPromotion = null;
  }

  backToDashboard(): void {
    this.showCreatePromotion = false;
    this.editingPromotion = null;
  }

  editPromotion(p: DashboardPromotion): void {
    this.editingPromotion = p;
    this.detailVisible = false;
    this.showCreatePromotion = true;
  }

  togglePromotionStatus(p: DashboardPromotion): void {
    const next = this.isActive(p.status) ? 'INACTIVE' : 'ACTIVE';
    this.vrboService.updatePromotion({
      id: p.id,
      propertyId: p.propertyId,
      status: next
    }).subscribe(
      () => {
        p.status = next;
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Promotion ${next.toLowerCase()} successfully` });
      },
      (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update promotion: ' + (err?.error?.message || err?.message || err) });
      }
    );
  }

  duplicatePromotion(p: DashboardPromotion): void {
    this.editingPromotion = { ...p, id: '' };  // Clear ID so it creates a new one
    this.showCreatePromotion = true;
  }

  /**
   * Finds all ACTIVE promotions of the same type across all properties and deactivates them.
   * Shows a confirmation dialog with the count before proceeding.
   */
  deactivateForAllProperties(p: DashboardPromotion): void {
    const matchingPromos = this.findMatchingActivePromotions(p);

    if (!matchingPromos.length) {
      this.messageService.add({ severity: 'info', summary: 'Nothing to deactivate',
        detail: `No active ${this.getPromotionTypeLabel(p.promotionType)} promotions found across properties.` });
      return;
    }

    const propertyCount = new Set(matchingPromos.map(m => m.propertyId)).size;
    const typeLabel = this.getPromotionTypeLabel(p.promotionType);

    this.confirmationService.confirm({
      header: 'Deactivate for All Properties',
      message: `This will deactivate ${matchingPromos.length} active "${typeLabel}" promotion${matchingPromos.length > 1 ? 's' : ''} across ${propertyCount} propert${propertyCount > 1 ? 'ies' : 'y'}. This action cannot be undone. Continue?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Deactivate All',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.executeBulkDeactivate(matchingPromos);
      },
      reject: () => {
        this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'Bulk deactivation cancelled.' });
      }
    });
  }

  /**
   * Collects all active promotions of the same type across every property.
   */
  private findMatchingActivePromotions(p: DashboardPromotion): DashboardPromotion[] {
    const matches: DashboardPromotion[] = [];
    for (const propId of Object.keys(this.promotionsByProperty)) {
      const promos = this.promotionsByProperty[propId] || [];
      for (const promo of promos) {
        if (promo.promotionType === p.promotionType && this.isActive(promo.status)) {
          matches.push(promo);
        }
      }
    }
    // Also check global wiki coupons if the promotion is a wiki coupon
    if (p.referralSource === 'WIKI' || p.promotionType === 'WIKI_COUPON') {
      for (const promo of this.globalWikiCoupons) {
        if (promo.promotionType === p.promotionType && this.isActive(promo.status)) {
          matches.push(promo);
        }
      }
    }
    return matches;
  }

  /**
   * Sequentially deactivates each promotion via the update API.
   */
  private executeBulkDeactivate(promos: DashboardPromotion[]): void {
    this.bulkDeactivating = true;
    this.bulkDeactivateProgress = 0;
    this.bulkDeactivateTotal = promos.length;
    let successCount = 0;
    let failCount = 0;

    const deactivateNext = (index: number) => {
      if (index >= promos.length) {
        this.bulkDeactivating = false;
        const summary = failCount === 0 ? 'Bulk Deactivation Complete' : 'Bulk Deactivation Complete (with errors)';
        const severity = failCount === 0 ? 'success' : 'warn';
        this.messageService.add({
          severity, summary,
          detail: `${successCount} deactivated, ${failCount} failed out of ${promos.length} total.`,
          life: 6000
        });
        // Refresh dashboard data to reflect changes
        this.loadDashboardData();
        return;
      }

      const promo = promos[index];
      this.bulkDeactivateProgress = index + 1;

      this.vrboService.updatePromotion({
        id: promo.id,
        propertyId: promo.propertyId,
        status: 'INACTIVE'
      }).subscribe(
        () => {
          promo.status = 'INACTIVE';
          successCount++;
          deactivateNext(index + 1);
        },
        (err: any) => {
          failCount++;
          console.error(`Failed to deactivate promotion ${promo.id} for property ${promo.propertyId}:`, err);
          deactivateNext(index + 1);
        }
      );
    };

    deactivateNext(0);
  }
}

